/**
 * LayersConstructionRows — the project-level construction (guide) line +
 * construction-group row builders and the construction drag-and-drop
 * re-parenting wiring, extracted from LayersPanel (#883, epic #828 slice
 * L-S2). LayersPanel OWNS a LayersConstructionRows instance and DRIVES it by
 * composition from `render()` (it is NOT a base class), mirroring the
 * LayersDecorationRows precedent (L-S1): this section holds the construction
 * cluster — the outer section header, the recursive group tree, the group
 * header (with inline rename), the per-line row, the group-delete, and the
 * construction-group drop-target wiring — while the `render()` dispatch that
 * decides WHEN to call `appendConstructionRows` stays on LayersPanel.
 *
 * Everything the builders read from the owning panel — the list DOM append
 * target, the editor context, the collapsed-set, the chip-visibility flag, the
 * broken-constraint id set, the in-flight construction drag payload, and the
 * shared row scaffolding (`makeRow` / `makeGroupHeader` free helpers, the
 * click-toggle / path-bounds / connection-chip / drag-source builders, the
 * `toggle` collapse handler, the reorder drop-strip emitter, and the
 * decoration-side tracing-image row) — is reached through the
 * `LayersConstructionHost` interface so the moved bodies stay byte-for-byte
 * identical. The shared `makeRow` / `makeGroupHeader` / `TRIANGLE_*` free
 * exports (used by BOTH decoration AND construction rows) stay
 * single-definition in `./LayersPanel` and are imported here. The
 * construction-only icon constant + drop-target validation helpers moved down
 * with the rows. Behaviour is identical to the in-panel original — this is a
 * pure move + host rewiring.
 */
import type { EditorContext } from "../tools/EditorContext";
import {
  moveConstructionItems,
  renameConstructionGroup,
  ungroupConstructionLines,
} from "../model/projectOps";
import {
  descendantConstructionGroupIds,
  descendantConstructionLineIds,
  groupAncestorIds,
  isVisible,
  projectConstructionGroups,
  projectConstructionLines,
  projectTracingImages,
  topLevelConstructionGroups,
  topLevelConstructionOrder,
  type ConstructionGroup,
  type ConstructionLine,
  type Project,
  type TracingImage,
} from "../model/Project";
import constructionSvg from "../icons/construction.svg?raw";
import { stripIconSvg } from "../icons/stripIconSvg";
import { pruneConstraintsForDeletedLines } from "../model/constraints";
import { CONNECTIONS, type ConnectionChipSpec } from "../model/connections";
import {
  TRIANGLE_DOWN,
  TRIANGLE_RIGHT,
  makeGroupHeader,
  makeRow,
} from "./layersRowKit";

// The construction glyph for a line's row. `construction.svg` carries its own
// `style="fill:currentColor"` now, so this is the plain shared normaliser —
// it used to strip and then re-inject a fill here, which is a second
// normaliser for a file the toolbar already draws (#216).
const CONSTRUCTION_ICON_HTML = stripIconSvg(constructionSvg);

/** In-flight construction drag payload (issue #80 layers-panel
 *  drag-and-drop), shared with the panel's drag-source + reorder-strip
 *  wiring. */
interface ConstructionDragPayload {
  lineIds: string[];
  groupIds: string[];
  imageIds: string[];
  sourceElement: HTMLElement;
}

export interface LayersConstructionHost {
  /** The `<ul>`/`<div>` list element every construction row is appended to. */
  getListEl(): HTMLElement;
  /** The shared editor context (history / commit / scene / selection). */
  getCtx(): EditorContext;
  /** Keys of currently-collapsed containers (shared mutable Set). */
  getCollapsed(): Set<string>;
  /** Whether the per-line constraint chip strips are shown. */
  getChipsVisible(): boolean;
  /** Coincident-constraint ids whose refs no longer share a position —
   *  recomputed once per `render()`, read by the per-line chip loop. */
  getBrokenConstraintIds(): ReadonlySet<string>;
  /** Payload of the in-flight construction drag (set by the panel's
   *  drag-source, read/cleared by the drop-target validation here). */
  getCurrentDragPayload(): ConstructionDragPayload | null;
  setCurrentDragPayload(v: ConstructionDragPayload | null): void;
  /** Toggle a container's collapsed state and re-render. */
  toggle(key: string): void;
  /** Register a collapsible container in the current render's tree (#873).
   *  Pass the header element for containers that carry an expand/collapse-all
   *  button (the section + group headers), else `null`. */
  registerCollapsible(
    key: string,
    parentKey: string | null,
    headerEl: HTMLElement | null,
  ): void;
  /** Expand or collapse every group nested under `key` in one action (#873). */
  expandCollapseAll(key: string): void;
  /** Make a construction/tracing row draggable for reordering. */
  installDragSource(
    el: HTMLElement,
    source: { kind: "line" | "group" | "image"; id: string },
  ): void;
  /** Emit one thin reorder drop-strip anchored at `(parentGroupId,
   *  insertBefore)`. Shared with the tracing-image pass, so it stays on
   *  the panel. */
  appendDropStrip(
    parentGroupId: string | null,
    insertBefore: { kind: "line" | "group" | "image"; id: string } | null,
    indentPx: number,
  ): void;
  /** Render one tracing-image leaf row (owned by the decoration section). */
  appendTracingImageRow(
    image: TracingImage,
    indentPx: number,
    sel: {
      constructionId?: string | null;
      constructionIds?: string[];
      constructionGroupIds?: string[];
      tracingImageIds?: string[];
    },
    track: (row: { element: HTMLElement; matchesSelection: boolean }) => void,
  ): void;
  /** Click-toggles-selection / dblclick-zooms factory shared by all rows. */
  buildClickToggle(opts: {
    isSelected: boolean;
    select: () => void;
    deselect: () => void;
    zoomBounds?: () => {
      x: number;
      y: number;
      width: number;
      height: number;
    } | null;
  }): {
    onSelect: () => void;
    onDoubleClick?: () => void;
  };
  /** Bounds of a parsed SVG-path item (for line dblclick-zoom). */
  pathDataBounds(pathData: string): {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null;
  /** Build one connection/constraint chip element. */
  buildConnectionChip(spec: ConnectionChipSpec): HTMLSpanElement;
}

export class LayersConstructionRows {
  constructor(private host: LayersConstructionHost) {}

  // — Injected panel services, reached through the host so the moved builder
  //   bodies stay byte-for-byte identical (`this.ctx` / `this.listEl` / … keep
  //   working). The real implementations stay single-definition on the panel. —
  private get ctx(): EditorContext {
    return this.host.getCtx();
  }
  private get listEl(): HTMLElement {
    return this.host.getListEl();
  }
  private get collapsed(): Set<string> {
    return this.host.getCollapsed();
  }
  private get chipsVisible(): boolean {
    return this.host.getChipsVisible();
  }
  private get brokenConstraintIds(): ReadonlySet<string> {
    return this.host.getBrokenConstraintIds();
  }
  private get currentDragPayload(): ConstructionDragPayload | null {
    return this.host.getCurrentDragPayload();
  }
  private set currentDragPayload(v: ConstructionDragPayload | null) {
    this.host.setCurrentDragPayload(v);
  }
  private toggle(key: string): void {
    this.host.toggle(key);
  }
  private registerCollapsible(
    key: string,
    parentKey: string | null,
    headerEl: HTMLElement | null,
  ): void {
    this.host.registerCollapsible(key, parentKey, headerEl);
  }
  private expandCollapseAll(key: string): void {
    this.host.expandCollapseAll(key);
  }
  private installDragSource(
    el: HTMLElement,
    source: { kind: "line" | "group" | "image"; id: string },
  ): void {
    this.host.installDragSource(el, source);
  }
  private appendDropStrip(
    parentGroupId: string | null,
    insertBefore: { kind: "line" | "group" | "image"; id: string } | null,
    indentPx: number,
  ): void {
    this.host.appendDropStrip(parentGroupId, insertBefore, indentPx);
  }
  private buildClickToggle(
    opts: Parameters<LayersConstructionHost["buildClickToggle"]>[0],
  ): ReturnType<LayersConstructionHost["buildClickToggle"]> {
    return this.host.buildClickToggle(opts);
  }
  private pathDataBounds(
    pathData: string,
  ): ReturnType<LayersConstructionHost["pathDataBounds"]> {
    return this.host.pathDataBounds(pathData);
  }
  private buildConnectionChip(spec: ConnectionChipSpec): HTMLSpanElement {
    return this.host.buildConnectionChip(spec);
  }

  private setConstructionVisibility(ids: Set<string>, visible: boolean) {
    this.ctx.commit("Toggle visibility", (proj) => ({
      ...proj,
      constructionLines: projectConstructionLines(proj).map((l) =>
        ids.has(l.id) ? { ...l, visible } : l,
      ),
    }));
  }

  /** Project-level construction (guide) lines. Renders the auto outer
   *  "Construction Lines (N)" section header, then any user-created
   *  ConstructionGroups as nested sub-headers (recursing into their
   *  child groups), then any ungrouped lines at the section level.
   *  Sits at the top of the layers list (above all parts) because
   *  guides are a project-wide concept, not a per-part feature. */
  public appendConstructionRows(
    project: Project,
    sel: {
      constructionId?: string | null;
      constructionIds?: string[];
      constructionGroupIds?: string[];
      tracingImageIds?: string[];
    },
    track: (row: { element: HTMLElement; matchesSelection: boolean }) => void,
  ) {
    const lines = projectConstructionLines(project);
    const allGroups = projectConstructionGroups(project);
    // Render the section when there are lines OR any groups — a group
    // can hold only tracing images (issue #509) with no lines, and it
    // still needs to show up (and stay droppable) in the panel.
    if (lines.length === 0 && allGroups.length === 0) return;

    const topLevel = topLevelConstructionGroups(project);
    // A line is "grouped" if any group anywhere in the tree contains
    // it directly. Ungrouped lines render at the section level after
    // the top-level groups.
    const groupedIds = new Set<string>();
    for (const g of allGroups) for (const id of g.lineIds) groupedIds.add(id);
    const ungrouped = lines.filter((l) => !groupedIds.has(l.id));

    // Outer section header — present whenever there's any structural
    // grouping to display, OR when there's more than one line.
    // Showing it for a single ungrouped line would be noise.
    const sectionKey = `construction:project`;
    const sectionOpen = !this.collapsed.has(sectionKey);
    const showSection = lines.length > 1 || allGroups.length > 0;
    if (showSection) {
      const ids = new Set(lines.map((l) => l.id));
      const anyVisible = lines.some((l) => isVisible(l));
      const header = makeGroupHeader({
        toggleGlyph: sectionOpen ? TRIANGLE_DOWN : TRIANGLE_RIGHT,
        onToggle: () => this.toggle(sectionKey),
        onExpandCollapseAll: () => this.expandCollapseAll(sectionKey),
        leadingHtml: CONSTRUCTION_ICON_HTML,
        hidden: !anyVisible,
        onLeadingClick: () => this.setConstructionVisibility(ids, !anyVisible),
        label: `Construction Lines (${lines.length})`,
        indentPx: 0,
        onDelete: () => {
          this.ctx.commit("Delete construction group", (proj) => ({
            ...proj,
            constructionLines: projectConstructionLines(proj).filter(
              (l) => !ids.has(l.id),
            ),
            constructionGroups: [],
            constraints: pruneConstraintsForDeletedLines(proj.constraints, ids),
          }));
          if (sel.constructionId && ids.has(sel.constructionId)) {
            this.ctx.setSelection({
              pieceId: null,
              decorationId: null,
              constructionId: null,
            });
          }
          this.collapsed.delete(sectionKey);
        },
      });
      header.setAttribute("data-construction-section", "project");
      this.installDropTarget(header, null);
      this.listEl.appendChild(header);
      // The Construction section is a collapsible container whose groups nest
      // under it (#873).
      this.registerCollapsible(sectionKey, null, header);
      if (!sectionOpen) return;
    }

    const baseIndent = showSection ? 16 : 0;
    // Single interleaved ordering at the top level (#887): top-level
    // groups and ungrouped lines walk ONE ordered sequence so a loose
    // line can sit above, below, or between groups — rather than the
    // old "all groups, then all loose lines" split that pinned every
    // group above every loose line. `topLevelConstructionOrder`
    // resolves the stored order (falling back to the legacy
    // groups-then-lines default for pre-#887 projects). Each entry emits
    // its reorder drop-strip (anchored at that entry) followed by the
    // group subtree or the line row; a trailing "end" strip closes the
    // section. The cursor's kind drives where the splice lands in
    // `moveConstructionItemsAt`.
    const topLevelById = new Map(topLevel.map((g) => [g.id, g]));
    const looseById = new Map(ungrouped.map((l) => [l.id, l]));
    for (const ref of topLevelConstructionOrder(project)) {
      if (ref.kind === "group") {
        const group = topLevelById.get(ref.id);
        if (!group) continue;
        this.appendDropStrip(null, { kind: "group", id: group.id }, baseIndent);
        this.appendConstructionGroupTree(
          project,
          group,
          baseIndent,
          sel,
          track,
          sectionKey,
        );
      } else {
        const line = looseById.get(ref.id);
        if (!line) continue;
        const idx = lines.indexOf(line);
        this.appendDropStrip(null, { kind: "line", id: line.id }, baseIndent);
        this.appendConstructionLineRow(line, idx, baseIndent, sel, track);
      }
    }
    this.appendDropStrip(null, null, baseIndent);
  }

  /** Recursive renderer for one group plus its descendants. Renders
   *  the group header at `indentPx`, then — if expanded — every
   *  member line and every child group one indent level deeper. The
   *  recursion gives nested groups a visual hierarchy without
   *  changing the flat DOM layout (every row remains a direct child
   *  of `this.root`; indentation is the only nesting signal). */
  private appendConstructionGroupTree(
    project: Project,
    group: ConstructionGroup,
    indentPx: number,
    sel: {
      constructionId?: string | null;
      constructionIds?: string[];
      constructionGroupIds?: string[];
      tracingImageIds?: string[];
    },
    track: (row: { element: HTMLElement; matchesSelection: boolean }) => void,
    /** Collapsible-container key this group nests under (#873): the section
     *  for a top-level group, or the parent group for a nested one. */
    parentKey: string,
  ) {
    this.appendConstructionGroupHeader(
      project,
      group,
      indentPx,
      sel,
      track,
      parentKey,
    );
    const groupKey = `construction-group:${group.id}`;
    const open = !this.collapsed.has(groupKey);
    if (!open) return;
    const childIndent = indentPx + 16;
    const lines = projectConstructionLines(project);
    const allGroups = projectConstructionGroups(project);
    const images = projectTracingImages(project);
    for (const lineId of group.lineIds) {
      const line = lines.find((l) => l.id === lineId);
      if (!line) continue;
      const idx = lines.indexOf(line);
      this.appendDropStrip(
        group.id,
        { kind: "line", id: line.id },
        childIndent,
      );
      this.appendConstructionLineRow(line, idx, childIndent, sel, track);
    }
    // Tracing-image members (issue #509) render after the group's
    // lines, as nested, draggable leaf rows.
    for (const imageId of group.imageIds ?? []) {
      const image = images.find((img) => img.id === imageId);
      if (!image) continue;
      this.appendDropStrip(
        group.id,
        { kind: "image", id: image.id },
        childIndent,
      );
      this.appendTracingImageRow(image, childIndent, sel, track);
    }
    for (const childGroupId of group.groupIds ?? []) {
      const child = allGroups.find((g) => g.id === childGroupId);
      if (!child) continue;
      this.appendDropStrip(
        group.id,
        { kind: "group", id: child.id },
        childIndent,
      );
      this.appendConstructionGroupTree(
        project,
        child,
        childIndent,
        sel,
        track,
        groupKey,
      );
    }
    // Trailing "end" strip — appends moving items to the end of this
    // parent's matching array. Only emitted when the group has any
    // direct children (an empty open group has nothing to reorder
    // against; the drop-on-header path already handles "drop into
    // empty group → first member"). Cosmetically the strip would
    // also be ambiguous with the group header below it in a flat
    // DOM.
    if (
      group.lineIds.length > 0 ||
      (group.imageIds ?? []).length > 0 ||
      (group.groupIds ?? []).length > 0
    ) {
      this.appendDropStrip(group.id, null, childIndent);
    }
  }

  /** Renders one construction-group header row. Body click selects all
   *  member lines (so the user can group-then-act); the triangle still
   *  toggles collapse/expand; the × dissolves the group (keeps the
   *  members as ungrouped lines); a label dblclick enters inline
   *  rename. */
  private appendConstructionGroupHeader(
    project: Project,
    group: ConstructionGroup,
    indentPx: number,
    sel: {
      constructionId?: string | null;
      constructionIds?: string[];
      constructionGroupIds?: string[];
    },
    track: (row: { element: HTMLElement; matchesSelection: boolean }) => void,
    /** Collapsible-container key this group nests under (#873). */
    parentKey: string,
  ) {
    const lines = projectConstructionLines(project);
    const descendantLineIds = descendantConstructionLineIds(project, group.id);
    const descendantSet = new Set(descendantLineIds);
    const memberLines = lines.filter((l) => descendantSet.has(l.id));
    const anyVisible = memberLines.some((l) => isVisible(l));
    const groupKey = `construction-group:${group.id}`;
    const open = !this.collapsed.has(groupKey);
    // Header reads as "selected" when this group is in the entity
    // selection set. Clicking on the header selects the group as an
    // entity (`constructionGroupIds`) WITHOUT folding its member lines
    // into `constructionIds` (#875) — the group is the transform unit,
    // members stay individually selectable. A `constructionGroupIds`
    // size check alone would let a sibling group's selection bleed into
    // this header's styling.
    const selectedGroupSet = new Set(sel.constructionGroupIds ?? []);
    const headerSelected = selectedGroupSet.has(group.id);
    const header = makeGroupHeader({
      toggleGlyph: open ? TRIANGLE_DOWN : TRIANGLE_RIGHT,
      onToggle: () => this.toggle(groupKey),
      onExpandCollapseAll: () => this.expandCollapseAll(groupKey),
      leadingHtml: CONSTRUCTION_ICON_HTML,
      hidden: !anyVisible,
      selected: headerSelected,
      onLeadingClick: () =>
        this.setConstructionVisibility(descendantSet, !anyVisible),
      label: group.name,
      indentPx,
      bodyClickToggles: false,
      onSelect: (ev: MouseEvent) => {
        const currentGroups = sel.constructionGroupIds ?? [];
        const currentLines = sel.constructionIds ?? [];
        if (ev.metaKey || ev.ctrlKey) {
          // Cmd/Ctrl-click toggles this group in/out of the entity
          // set. Per the per-item selection model (#875) the group is
          // the transform unit — its descendant member lines are NOT
          // folded into `constructionIds` (they'd otherwise blanket-
          // highlight and hide the fact that each member is still
          // independently selectable). Any descendant lines already in
          // the individual set are stripped so the group entity is the
          // single source of truth for its members.
          const inGroups = currentGroups.includes(group.id);
          const nextGroups = inGroups
            ? currentGroups.filter((id) => id !== group.id)
            : [...currentGroups, group.id];
          const nextLines = currentLines.filter((id) => !descendantSet.has(id));
          this.ctx.setSelection({
            pieceId: null,
            decorationId: null,
            constructionIds: nextLines,
            constructionGroupIds: nextGroups,
          });
          return;
        }
        // Plain click → single-select the group as an entity. Members
        // are not individually selected (#875): `constructionIds` stays
        // empty so a follow-up click on a child row can subselect just
        // that member (crucial for a one-member group).
        this.ctx.setSelection({
          pieceId: null,
          decorationId: null,
          constructionIds: [],
          constructionGroupIds: [group.id],
        });
      },
      onLabelDoubleClick: () => this.beginGroupRename(header, group),
      onHover: (entering) => {
        // Hovering the group header lights up every descendant line
        // on the canvas, so the user sees what the group encompasses
        // before clicking. Reuses the same `line`-kind hover overlay
        // path the individual line rows trigger.
        if (!entering) {
          this.ctx.scene.setConstructionConstraintHover(null);
          return;
        }
        const refs = descendantLineIds.map((id) => ({
          kind: "line" as const,
          constructionId: id,
        }));
        if (refs.length === 0) {
          this.ctx.scene.setConstructionConstraintHover(null);
          return;
        }
        this.ctx.scene.setConstructionConstraintHover({ refs });
      },
      onDelete: () => {
        this.deleteConstructionGroup(group);
      },
    });
    header.setAttribute("data-construction-group-id", group.id);
    this.installDragSource(header, { kind: "group", id: group.id });
    this.installDropTarget(header, group.id);
    this.listEl.appendChild(header);
    // A construction group is a collapsible container nested under its parent
    // (the section or an enclosing group); its own child groups nest under it
    // (#873).
    this.registerCollapsible(groupKey, parentKey, header);
    if (headerSelected) {
      track({ element: header, matchesSelection: true });
    }
  }

  /** × on a construction group wipes the whole subtree — the group,
   *  every nested subgroup, and every line transitively inside any
   *  of them. Users can undo, or use the dedicated Ungroup button /
   *  Cmd+Shift+G if they wanted to dissolve the group while keeping
   *  the lines. */
  private deleteConstructionGroup(group: ConstructionGroup) {
    const project = this.ctx.history.state;
    const descendantLineIds = descendantConstructionLineIds(project, group.id);
    const descendantGroupIds = descendantConstructionGroupIds(
      project,
      group.id,
    );
    const lineIdSet = new Set(descendantLineIds);
    const groupIdSet = new Set(descendantGroupIds);
    this.ctx.commit("Delete construction group with lines", (proj) => ({
      ...proj,
      constructionLines: projectConstructionLines(proj).filter(
        (l) => !lineIdSet.has(l.id),
      ),
      constructionGroups: projectConstructionGroups(proj)
        .filter((g) => !groupIdSet.has(g.id))
        .map((g) => ({
          // Strip dangling references from surviving groups — any
          // group that had one of the deleted groups as a child
          // (cross-tree the deleted subtree shouldn't appear, but
          // be defensive against corrupted parent references).
          ...g,
          groupIds: (g.groupIds ?? []).filter((gid) => !groupIdSet.has(gid)),
        }))
        .map((g) =>
          g.groupIds && g.groupIds.length === 0
            ? { ...g, groupIds: undefined }
            : g,
        ),
      constraints: pruneConstraintsForDeletedLines(proj.constraints, lineIdSet),
    }));
    // Drop any deleted line / group from the live selection so
    // the properties panel doesn't try to render a stale id.
    const sel = this.ctx.scene.getSelection();
    const remainingLines = (sel.constructionIds ?? []).filter(
      (id) => !lineIdSet.has(id),
    );
    const remainingGroups = (sel.constructionGroupIds ?? []).filter(
      (id) => !groupIdSet.has(id),
    );
    if (
      remainingLines.length !== (sel.constructionIds ?? []).length ||
      remainingGroups.length !== (sel.constructionGroupIds ?? []).length
    ) {
      this.ctx.setSelection({
        pieceId: null,
        decorationId: null,
        constructionIds: remainingLines,
        constructionGroupIds: remainingGroups,
      });
    }
  }

  /** Wires a row as a drop target — group headers re-parent into the
   *  group, the section header re-parents to top-level
   *  (`targetParentGroupId === null`). Validates the in-flight drag
   *  on `dragover` (cycle prevention, no-op self-drop) so only
   *  meaningful targets light up. Commits via `moveConstructionItems`
   *  on `drop`, then nudges the selection so the moved items become
   *  the current focus. */
  private installDropTarget(
    el: HTMLElement,
    targetParentGroupId: string | null,
  ): void {
    el.addEventListener("dragover", (ev) => {
      if (!this.canDropOn(targetParentGroupId)) return;
      ev.preventDefault();
      if (ev.dataTransfer) ev.dataTransfer.dropEffect = "move";
      el.classList.add("drop-target-active");
    });
    el.addEventListener("dragleave", (ev) => {
      // Ignore dragleave bubbling from child elements — the relatedTarget
      // is the element the cursor moved to. If it's still inside this
      // drop target, keep the highlight on.
      const next = (ev as DragEvent).relatedTarget as Node | null;
      if (next && el.contains(next)) return;
      el.classList.remove("drop-target-active");
    });
    el.addEventListener("drop", (ev) => {
      el.classList.remove("drop-target-active");
      const payload = this.currentDragPayload;
      this.currentDragPayload = null;
      if (!payload) return;
      if (!this.canDrop(payload, targetParentGroupId)) return;
      ev.preventDefault();
      ev.stopPropagation();
      this.ctx.commit("Move construction items", (proj) =>
        moveConstructionItems(
          proj,
          {
            lineIds: payload.lineIds,
            groupIds: payload.groupIds,
            imageIds: payload.imageIds,
          },
          targetParentGroupId,
        ),
      );
      this.ctx.setSelection({
        pieceId: null,
        decorationId: null,
        constructionIds: payload.lineIds,
        constructionGroupIds: payload.groupIds,
        tracingImageIds: payload.imageIds,
      });
    });
  }

  /** Returns true when there's an in-flight drag AND the payload is
   *  valid for dropping into `targetParentGroupId`. "Valid" means:
   *  not a self-drop (group ID === target), not a cycle (target is
   *  not a descendant of any payload group), and at least one item
   *  would actually move (something is not already a direct child of
   *  the target). */
  private canDropOn(targetParentGroupId: string | null): boolean {
    const payload = this.currentDragPayload;
    if (!payload) return false;
    return this.canDrop(payload, targetParentGroupId);
  }

  public canDrop(
    payload: { lineIds: string[]; groupIds: string[]; imageIds: string[] },
    targetParentGroupId: string | null,
  ): boolean {
    const project = this.ctx.history.state;
    const targetGroup =
      targetParentGroupId !== null
        ? projectConstructionGroups(project).find(
            (g) => g.id === targetParentGroupId,
          )
        : null;
    const targetLineSet = new Set(targetGroup?.lineIds ?? []);
    const targetGroupSet = new Set(targetGroup?.groupIds ?? []);
    const targetImageSet = new Set(targetGroup?.imageIds ?? []);
    // Top-level: line/image is "already top-level" if not in any
    // group; group is "already top-level" if not a child of any parent.
    const allGroups = projectConstructionGroups(project);
    const groupedLineIds = new Set<string>();
    const groupedImageIds = new Set<string>();
    for (const g of allGroups) {
      for (const id of g.lineIds) groupedLineIds.add(id);
      for (const id of g.imageIds ?? []) groupedImageIds.add(id);
    }
    const childGroupIds = new Set<string>();
    for (const g of allGroups) {
      for (const id of g.groupIds ?? []) childGroupIds.add(id);
    }
    // Cycle / self-drop check for groups.
    if (targetParentGroupId !== null) {
      const targetAncestors = new Set([
        ...groupAncestorIds(project, targetParentGroupId),
        targetParentGroupId,
      ]);
      for (const gid of payload.groupIds) {
        if (!targetAncestors.has(gid)) {
          // Group can be dropped here if it isn't already a direct
          // child of the target.
          if (!targetGroupSet.has(gid)) return true;
        }
      }
      for (const lid of payload.lineIds) {
        if (!targetLineSet.has(lid)) return true;
      }
      for (const iid of payload.imageIds) {
        if (!targetImageSet.has(iid)) return true;
      }
      return false;
    }
    // Top-level: at least one item must move.
    for (const gid of payload.groupIds) {
      if (childGroupIds.has(gid)) return true;
    }
    for (const lid of payload.lineIds) {
      if (groupedLineIds.has(lid)) return true;
    }
    for (const iid of payload.imageIds) {
      if (groupedImageIds.has(iid)) return true;
    }
    return false;
  }

  /** Swaps the group header's label span for an inline <input>; commits
   *  the new name on Enter or blur, restores the panel on Escape. The
   *  panel re-renders on commit so we don't manually tear down the
   *  input — `render()` will replace the header with a fresh one. */
  private beginGroupRename(header: HTMLDivElement, group: ConstructionGroup) {
    const label = header.querySelector(".row-label") as HTMLSpanElement | null;
    if (!label) return;
    const input = document.createElement("input");
    input.type = "text";
    input.value = group.name;
    input.className = "row-label-input";
    label.replaceWith(input);
    input.focus();
    input.select();
    let committed = false;
    const commit = () => {
      if (committed) return;
      committed = true;
      const next = input.value;
      if (next.trim() === group.name) {
        // No change — restore the original label without a commit so
        // history doesn't accumulate no-op rename entries.
        input.replaceWith(label);
        return;
      }
      this.ctx.commit("Rename construction group", (proj) =>
        renameConstructionGroup(proj, group.id, next),
      );
    };
    input.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        commit();
      } else if (ev.key === "Escape") {
        ev.preventDefault();
        committed = true;
        input.replaceWith(label);
      }
      ev.stopPropagation();
    });
    input.addEventListener("blur", commit);
    input.addEventListener("click", (ev) => ev.stopPropagation());
  }

  private appendConstructionLineRow(
    line: ConstructionLine,
    originalIndex: number,
    indentPx: number,
    sel: { constructionId?: string | null; constructionIds?: string[] },
    track: (row: { element: HTMLElement; matchesSelection: boolean }) => void,
  ) {
    // Highlight every line that's in the multi-select set, not just
    // the lead — otherwise group-via-shortcut would leave only the
    // last-clicked row visually selected after the multi-select.
    const selectedIds = sel.constructionIds ?? [];
    const matches = selectedIds.includes(line.id);
    const isLead = sel.constructionId === line.id;
    const visible = isVisible(line);
    const zoomBounds = () => this.pathDataBounds(line.pathData);
    // Plain-click single-select with the buildClickToggle deselect-
    // on-second-click behaviour kept intact, so users who never use
    // modifiers see no change. Modifier clicks divert into the
    // multi-select branch below before this fires.
    const baseClick = this.buildClickToggle({
      isSelected: isLead && selectedIds.length === 1,
      select: () =>
        // Subselect just this line (#875). Auto-promote is disabled so
        // that selecting the SOLE member of a one-item group does not
        // silently snap back to the enclosing group — the whole point
        // is to reach that single member's Properties panel. For a
        // line that only partially fills its group, auto-promote never
        // fired anyway, so this is a no-op there.
        this.ctx.setSelection(
          {
            pieceId: null,
            decorationId: null,
            constructionIds: [line.id],
          },
          { autoPromoteGroups: false },
        ),
      deselect: () =>
        this.ctx.setSelection({
          pieceId: null,
          decorationId: null,
          constructionIds: [],
        }),
      zoomBounds,
    });
    const onSelect = (ev: MouseEvent) => {
      if (ev.metaKey || ev.ctrlKey) {
        // Cmd/Ctrl-click toggles the line in or out of the current
        // multi-selection without touching the rest.
        const next = selectedIds.includes(line.id)
          ? selectedIds.filter((id) => id !== line.id)
          : [...selectedIds, line.id];
        this.ctx.setSelection({
          pieceId: null,
          decorationId: null,
          constructionIds: next,
        });
        return;
      }
      if (ev.shiftKey && selectedIds.length > 0) {
        // Shift-click range-selects between the current lead and the
        // clicked line in project display order. The lead stays the
        // clicked line so a follow-up shift-click extends from there.
        const all = projectConstructionLines(this.ctx.history.state).map(
          (l) => l.id,
        );
        const leadId =
          sel.constructionId ?? selectedIds[selectedIds.length - 1]!;
        const a = all.indexOf(leadId);
        const b = all.indexOf(line.id);
        if (a >= 0 && b >= 0) {
          const [lo, hi] = a <= b ? [a, b] : [b, a];
          const range = all.slice(lo, hi + 1);
          // Preserve every previously-selected id that's outside the
          // new range, then append the range so the clicked line is
          // the new lead.
          const carryover = selectedIds.filter((id) => !range.includes(id));
          const next =
            a <= b
              ? [...carryover, ...range]
              : [...carryover, ...range.reverse()];
          this.ctx.setSelection({
            pieceId: null,
            decorationId: null,
            constructionIds: next,
          });
          return;
        }
      }
      baseClick.onSelect();
    };
    // Connection chips (issue #180 unification) — driven by the
    // Connections registry. Each registered kind (snap, offset,
    // mirror) contributes its chip specs for this line; we render
    // them in one loop so adding a fourth kind later is one new
    // entry in the registry, not three more loops here.
    const chipSpecs: ConnectionChipSpec[] = [];
    if (this.chipsVisible) {
      const allLines = projectConstructionLines(this.ctx.history.state);
      for (const conn of CONNECTIONS) {
        chipSpecs.push(
          ...conn.chipsForLine(line, this.ctx.history.state, {
            allLines,
            brokenIds: this.brokenConstraintIds,
          }),
        );
      }
    }
    let trailing: HTMLElement | undefined;
    if (chipSpecs.length > 0) {
      trailing = document.createElement("span");
      trailing.className = "constraint-chip-strip";
      for (const spec of chipSpecs) {
        trailing.appendChild(this.buildConnectionChip(spec));
      }
    }
    const row = makeRow({
      // `leadingGlyph` is required by RowOptions; empty string lets the
      // `leadingHtml` icon take over the slot the way decoration rows do.
      leadingGlyph: "",
      leadingHtml: CONSTRUCTION_ICON_HTML,
      hidden: !visible,
      onLeadingClick: () =>
        this.setConstructionVisibility(new Set([line.id]), !visible),
      label: line.name ?? `Construction ${originalIndex + 1}`,
      selected: matches,
      indentPx,
      onSelect,
      onDoubleClick: baseClick.onDoubleClick,
      // Test hook — pin the construction line's id to the row so
      // e2e specs can locate exactly the row they want to act on
      // without depending on icon HTML or sibling traversal.
      dataAttributes: { "data-construction-id": line.id },
      trailing,
      onHover: (entering) => {
        // Paint a yellow halo over this line while the pointer is
        // on its row, so the parts list and the canvas stay in
        // lock-step about which line the user is pointing at. Same
        // overlay path the constraint-hover uses, called with a
        // `line`-kind ref so the whole stroke lights up.
        this.ctx.scene.setConstructionConstraintHover(
          entering
            ? { refs: [{ kind: "line", constructionId: line.id }] }
            : null,
        );
      },
      onDelete: () => {
        const all = projectConstructionLines(this.ctx.history.state);
        const next =
          all[originalIndex + 1]?.id ?? all[originalIndex - 1]?.id ?? null;
        this.ctx.commit("Delete construction line", (proj) => {
          const stripped = ungroupConstructionLines(proj, [line.id]);
          return {
            ...stripped,
            constructionLines: projectConstructionLines(stripped).filter(
              (l) => l.id !== line.id,
            ),
            constraints: pruneConstraintsForDeletedLines(
              stripped.constraints,
              new Set([line.id]),
            ),
          };
        });
        this.ctx.setSelection({
          pieceId: null,
          decorationId: null,
          constructionId: next,
        });
      },
    });
    this.installDragSource(row, { kind: "line", id: line.id });
    this.listEl.appendChild(row);
    track({ element: row, matchesSelection: isLead });
  }

  private appendTracingImageRow(
    image: TracingImage,
    indentPx: number,
    sel: {
      constructionId?: string | null;
      constructionIds?: string[];
      constructionGroupIds?: string[];
      tracingImageIds?: string[];
    },
    track: (row: { element: HTMLElement; matchesSelection: boolean }) => void,
  ): void {
    this.host.appendTracingImageRow(image, indentPx, sel, track);
  }
}
